import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { useContactForm } from '@/hooks/useSupabase';
import { MapPin, Linkedin, FileText, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export function ContactFormSection() {
  const { t, i18n } = useTranslation('contact');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';

  // Recalculer le schéma Zod quand la langue change
  const contactSchema = useMemo(() => z.object({
    name: z.string().min(2, t('form.fields.name.error')),
    email: z.string().email(t('form.fields.email.error')),
    phone: z.string().optional(),
    company: z.string().optional(),
    message: z
      .string()
      .min(10, t('form.fields.message.error')),
  }), [t, i18n.language]);

  type ContactFormData = z.infer<typeof contactSchema>;

  const contactMutation = useContactForm();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      await contactMutation.mutateAsync({
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        company: data.company || null,
        message: data.message,
      });
      reset();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Intro */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-4xl font-heading font-bold text-aspexit-black mb-6">
            {t('form.title')}
          </h2>
          <p className="text-lg text-aspexit-gray">
            {t('form.subtitle')}
          </p>
        </div>

        {/* Form + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-12 max-w-6xl mx-auto">
          {/* Form */}
          <Card className="p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <div>
                <Label htmlFor="name">
                  {t('form.fields.name.label')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder={t('form.fields.name.placeholder')}
                  className={`mt-2 ${errors.name ? 'border-red-500' : ''}`}
                />
                {errors.name && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="email">
                  {t('form.fields.email.label')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder={t('form.fields.email.placeholder')}
                  className={`mt-2 ${errors.email ? 'border-red-500' : ''}`}
                />
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">{t('form.fields.phone.label')}</Label>
                  <Input
                    id="phone"
                    {...register('phone')}
                    placeholder={t('form.fields.phone.placeholder')}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="company">{t('form.fields.company.label')}</Label>
                  <Input
                    id="company"
                    {...register('company')}
                    placeholder={t('form.fields.company.placeholder')}
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message">
                  {t('form.fields.message.label')} <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="message"
                  {...register('message')}
                  rows={8}
                  placeholder={t('form.fields.message.placeholder')}
                  className={`mt-2 ${errors.message ? 'border-red-500' : ''}`}
                />
                {errors.message && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.message.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-aspexit-green hover:bg-aspexit-green-dark"
                disabled={contactMutation.isPending}
              >
                {contactMutation.isPending
                  ? t('form.submit.sending')
                  : t('form.submit.idle')}
              </Button>

              {contactMutation.isSuccess && (
                <p className="text-sm text-aspexit-green text-center">
                  {t('form.submit.success')}
                </p>
              )}

              {contactMutation.isError && (
                <p className="text-sm text-red-500 text-center">
                  {t('form.submit.error')}
                </p>
              )}

              <p className="text-sm text-aspexit-gray text-center">
                {t('form.footer')}
              </p>
            </form>
          </Card>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <Card className="p-6">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-aspexit-green flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-semibold text-aspexit-gray uppercase tracking-wide mb-1">
                      {t('sidebar.address.label')}
                    </p>
                    <p className="text-aspexit-black text-sm">
                      {t('sidebar.address.street')}
                      <br />
                      {t('sidebar.address.city')}
                      <br />
                      {t('sidebar.address.country')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Linkedin className="w-6 h-6 text-aspexit-green flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-semibold text-aspexit-gray uppercase tracking-wide mb-1">
                      {t('sidebar.linkedin.label')}
                    </p>
                    <a
                      href="https://www.linkedin.com/in/corentin-leroux-0225a182/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-aspexit-green hover:underline flex items-center gap-1"
                    >
                      Corentin Leroux
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <FileText className="w-6 h-6 text-aspexit-green flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-semibold text-aspexit-gray uppercase tracking-wide mb-1">
                      {t('sidebar.cv.label')}
                    </p>
                    <a
                      href="/cv-corentin-leroux.pdf"
                      download
                      className="text-aspexit-green hover:underline flex items-center gap-1"
                    >
                      {t('sidebar.cv.download')}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </Card>

            {/* Wiki Agri Tech Highlight */}
            <Card className="relative overflow-hidden border-2 border-border hover:shadow-lg transition-shadow">
              {/* Barre verticale verte sur le côté gauche */}
              <div className="absolute left-0 top-0 bottom-0 w-2 bg-aspexit-green" />

              <div className="p-6 text-center">
                {/* Logo WAT SVG */}
                <div className="mx-auto mb-3 flex justify-center">
                  <svg viewBox="0 0 300 100" className="h-12" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Wiki Agri Tech">
                    <text x="10" y="40" fill="#0066CC" fontSize="32" fontWeight="bold" fontFamily="Arial, sans-serif">WIKI</text>
                    <text x="10" y="72" fill="#0066CC" fontSize="32" fontWeight="bold" fontFamily="Arial, sans-serif">AGRI TECH</text>
                    <text x="10" y="90" fill="#0066CC" fontSize="11" fontWeight="normal" fontFamily="Arial, sans-serif" letterSpacing="2">AGRICULTURE & NUMÉRIQUE</text>
                  </svg>
                </div>

                {/* Badge */}
                <div className="inline-block px-3 py-1 bg-aspexit-green/10 text-aspexit-green text-xs font-medium rounded-full mb-3">
                  {t('sidebar.wikiAgriTech.badge')}
                </div>

                {/* Description */}
                <p className="text-sm text-aspexit-gray mb-4">
                  {t('sidebar.wikiAgriTech.description')}
                </p>

                {/* Stats compactes */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-aspexit-gray-light rounded-lg p-2">
                    <div className="text-lg font-bold text-aspexit-green">2500+</div>
                    <div className="text-xs text-aspexit-gray">{t('sidebar.wikiAgriTech.stats.tools')}</div>
                  </div>
                  <div className="bg-aspexit-gray-light rounded-lg p-2">
                    <div className="text-lg font-bold text-aspexit-green">1K3+</div>
                    <div className="text-xs text-aspexit-gray">{t('sidebar.wikiAgriTech.stats.companies')}</div>
                  </div>
                </div>

                {/* CTA */}
                <a
                  href="https://wiki-agri-tech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-aspexit-green text-white rounded-lg hover:bg-aspexit-green-dark transition-colors text-sm font-medium w-full"
                >
                  {t('sidebar.wikiAgriTech.cta')}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
